import { CLOUD_API_URL } from '../config/serverConfig';
import { useMaestroStore } from '../store/maestroStore';
import type { EntityEvent, EntityKind, EntitySource, EntityType } from '../store/entities/entityTypes';
import { logAlbumNavBreadcrumb, logAlbumNavFailure } from '../utils/albumNavigationDiagnostics';

const ENTITY_DEBUG =
  typeof window !== 'undefined' &&
  (() => {
    try {
      return window.localStorage.getItem('maestro_entity_debug') === '1';
    } catch {
      return false;
    }
  })();

// Avoid scheduling repeated discography refresh loops for the same id.
const discographyRefreshLastStart = new Map<string, number>(); // id -> ts

function log(...args: any[]) {
  if (ENTITY_DEBUG) console.log('[EntityLoader]', ...args);
}

function now(): number {
  return Date.now();
}

function makeRequestId(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function makeKey(entityType: EntityType, id?: string | null, name?: string | null): string {
  const raw = id || name || 'unknown';
  // Normalize name keys so we don't create multiple cache entries for punctuation variants.
  const normalizedName = String(raw)
    .trim()
    .replace(/^\s*[-–—]+\s*/, '')
    .replace(/\s+/g, ' ')
    // Normalize diacritics so "Béla" and "Bela" share the same key.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return `${entityType}:${normalizedName}`;
}

function dispatch(event: EntityEvent) {
  // Minimal debug logging (temporary)
  if (ENTITY_DEBUG) {
    const k = (event as any).key;
    console.log('[EntityEvent]', event.type, event.entityType, k, event.meta.source, event.meta.requestId);
  }
  useMaestroStore.getState().dispatchEntityEvent(event);
}

function meta(requestId: string, source: EntitySource, triggeredBy: EntityEvent['meta']['triggeredBy']): EntityEvent['meta'] {
  return { ts: now(), requestId, source, triggeredBy };
}

function isUuid(id: string | null | undefined): boolean {
  if (!id) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

function isArtistStub(a: any): boolean {
  if (!a?.name) return true;
  const bio = a?.biographyNarrative || a?.biography || '';
  const templateScore = a?.templateScore ?? a?.enrichment?.templateScore ?? 0;
  const needs = a?.enrichment?.needsEnrichment;
  return (!bio || bio.length < 200) && (templateScore === 0 || needs);
}

function isTemplateReadyText(text: string): boolean {
  const bio = String(text || '');
  // Maestro "final" bios are sectioned + divider based.
  // We intentionally key off structure, not provenance, to avoid rendering raw Wikipedia dumps.
  return bio.includes('## Overview') && bio.includes('---') && bio.length >= 600;
}

function isDisplayableBio(text: string): boolean {
  const bio = String(text || '').trim();
  // Never render explicit Wikipedia-attributed placeholder bios.
  // Those should be treated as "missing" so we wait for Claude template enrichment.
  if (/\bsource:\s*wikipedia\b/i.test(bio)) return false;
  // Avoid obvious Wikipedia dumps/hatnotes.
  if (/\bthis article is about\b/i.test(bio)) return false;
  if (/\bmay refer to:\b/i.test(bio)) return false;
  // Allow short teasers so the page is immediately useful while enrichment runs.
  // The template gate is enforced separately for "done" / stopping refresh.
  return bio.length >= 220;
}

function extractAnyBio(payload: any): string {
  return String(
    payload?.biographyNarrative ||
      payload?.biography_narrative ||
      payload?.biography ||
      payload?.bio ||
      ''
  );
}

function hasTeaserWithoutFullBio(payload: any): boolean {
  const teaser = String(payload?.storyTeaser || payload?.bioSummary || '').trim();
  const full = String(payload?.biographyNarrative || payload?.biography || '').trim();
  // If we have "full" text but it's not template-ready (common for KV-seeded payloads),
  // treat it as teaser so the loader continues to enrich/refresh until the real template arrives.
  if (full && !isTemplateReadyText(full)) return true;
  return !!teaser && !full;
}

function isQueuedStatus(v: unknown): boolean {
  const s = String(v || '').toLowerCase();
  return s === 'queued' || s === 'enriching' || s === 'generating' || s === 'processing';
}

async function pollStoryChunks(args: {
  entityType: 'artist' | 'person';
  entityId: string;
  key: string;
  requestId: string;
  triggeredBy: 'ui' | 'baton' | 'system';
  stopWhen?: () => boolean;
}): Promise<void> {
  // DISABLED: Story chunk polling was causing 17 requests per artist page.
  // The full bio arrives with the main artist payload after enrichment completes.
  // Users can refresh the page if they want updated content.
  return;

  const { entityType, entityId, key, requestId, triggeredBy } = args;
  const stopWhen = args.stopWhen || (() => false);
  let afterSeq = -1;
  let chunks: any[] = [];
  const start = Date.now();

  // Fast early polling, then back off. Total window ~3 minutes.
  const scheduleMs = [800, 1500, 2200, 3000, 4200, 6000, 8500, 12000, 18000, 26000, 38000, 52000, 70000, 90000, 120000, 150000, 180000];

  const endpoint =
    entityType === 'artist'
      ? `${CLOUD_API_URL}/api/artists/${encodeURIComponent(entityId)}/story/chunks`
      : `${CLOUD_API_URL}/api/people/${encodeURIComponent(entityId)}/story/chunks`;

  const doPoll = async (): Promise<{ complete: boolean }> => {
    try {
      const res = await fetch(`${endpoint}?afterSeq=${encodeURIComponent(String(afterSeq))}&_cb=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return { complete: false };
      const j = await safeJson(res);
      const incoming = Array.isArray(j?.chunks) ? j.chunks : [];
      const complete = !!j?.complete || !!j?.meta?.complete;
      const error = j?.error || j?.meta?.error || null;

      if (incoming.length > 0) {
        // Append new chunks in sequence order, de-duping by seq.
        const bySeq = new Map<number, any>();
        for (const c of chunks) {
          const s = Number(c?.seq);
          if (Number.isFinite(s)) bySeq.set(s, c);
        }
        for (const c of incoming) {
          const s = Number(c?.seq);
          if (Number.isFinite(s) && !bySeq.has(s)) bySeq.set(s, c);
        }
        chunks = Array.from(bySeq.values()).sort((a, b) => Number(a?.seq) - Number(b?.seq));
        const last = chunks[chunks.length - 1];
        afterSeq = Number(last?.seq);

        dispatch({
          type: 'entity/enrichedReceived',
          entityType,
          key,
          payload: { biographyChunks: chunks, storyChunkStatus: error ? 'error' : (complete ? 'complete' : 'streaming') },
          meta: meta(requestId, 'enriched', triggeredBy),
        });

        return { complete: complete || !!error };
      }

      if (complete || error) {
        dispatch({
          type: 'entity/enrichedReceived',
          entityType,
          key,
          payload: { storyChunkStatus: error ? 'error' : 'complete' },
          meta: meta(requestId, 'enriched', triggeredBy),
        });
        return { complete: true };
      }
    } catch {
      // ignore
    }
    return { complete: false };
  };

  (async () => {
    for (let i = 0; i < scheduleMs.length; i++) {
      if (stopWhen()) return;
      const elapsed = Date.now() - start;
      const waitMs = Math.max(0, scheduleMs[i] - elapsed);
      await new Promise((r) => setTimeout(r, waitMs));
      if (stopWhen()) return;
      const { complete } = await doPoll();
      if (complete) return;
    }
  })();
}

function shouldThrottleFreshness(entityType: 'artist' | 'person', id: string): boolean {
  try {
    const key = `maestro_freshness_${entityType}_${id}`;
    const last = parseInt(window.localStorage.getItem(key) || '0', 10) || 0;
    // Throttle freshness checks to once per 7 days per entity per device.
    const THROTTLE_MS = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - last < THROTTLE_MS) return true;
    window.localStorage.setItem(key, String(Date.now()));
    return false;
  } catch {
    return false;
  }
}

function isPersonStub(p: any): boolean {
  if (!p?.name) return true;
  const bio = p?.biographyNarrative || p?.biography || '';
  return !bio || bio.length < 200;
}

async function safeJson(res: Response): Promise<any | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function countBracketLinks(text: string): number {
  const s = String(text || '');
  if (!s.includes('[[')) return 0;
  const re = /\[\[([^\]]+)\]\]/g;
  const seen = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    const nm = String(m[1] || '').trim();
    if (!nm) continue;
    const k = nm.replace(/[’`´]/g, "'").replace(/\s+/g, ' ').toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    if (seen.size >= 50) break;
  }
  return seen.size;
}

function countLinkMapKeys(linkMap: any): number {
  try {
    return linkMap && typeof linkMap === 'object' ? Object.keys(linkMap).length : 0;
  } catch {
    return 0;
  }
}

function scheduleLinkMapRefresh(args: {
  entityType: 'artist' | 'person';
  lookupKey: string;
  key: string;
  requestId: string;
  triggeredBy: 'ui' | 'baton' | 'system';
  initialPayload: any;
}): void {
  // DISABLED: Link map refresh was scheduling 5 duplicate artist fetches per page.
  // This was causing 5 extra requests at [5, 15, 35, 70, 120] seconds.
  // The linkMap is already included in the main artist response.
  // If users want updated links, they can refresh the page.
  return;

  const { entityType, lookupKey, key, requestId, triggeredBy, initialPayload } = args;
  try {
    const bio = String(initialPayload?.biographyNarrative || initialPayload?.biography || '').trim();
    if (!bio || !bio.includes('[[')) return;
    const bracketCount = countBracketLinks(bio);
    const lmCount = countLinkMapKeys(initialPayload?.linkMap);
    if (bracketCount === 0 || lmCount >= bracketCount) return;

    const endpoint =
      entityType === 'artist'
        ? `${CLOUD_API_URL}/api/artists/${encodeURIComponent(lookupKey)}?_cb=${Date.now()}`
        : `${CLOUD_API_URL}/api/contributors/${encodeURIComponent(lookupKey)}?_cb=${Date.now()}`;

    // Keep trying while the user is likely still reading.
    // MBID cache warming / background jobs can take longer than a single refresh window.
    const delays = [5000, 15000, 35000, 70000, 120000];
    for (const ms of delays) {
      setTimeout(async () => {
        try {
          const res = await fetch(endpoint, { cache: 'no-store' });
          if (!res.ok) return;
          const refreshed = await safeJson(res);
          if (!refreshed?.name) return;
          dispatch({ type: 'entity/enrichedReceived', entityType, key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
        } catch {
          // ignore
        }
      }, ms);
    }
  } catch {
    // ignore
  }
}

export async function loadArtist(args: {
  artistId?: string | null;
  artistName?: string | null;
  mbid?: string | null;
  typeHint?: string | null;
  triggeredBy?: 'ui' | 'baton' | 'system';
  hintKind?: EntityKind;
  preloadedPayload?: Record<string, any> | null;
}): Promise<{ key: string; requestId: string }> {
  const triggeredBy = args.triggeredBy || 'ui';
  const safeName = String(args.artistName || '').replace(/^\s*[-–—]+\s*/, '').trim();
  const rawId = String(args.artistId || '').trim();
  // Some navigation paths pass a name as "artistId" with artistName=null.
  // If it's not a UUID, treat it as a usable lookup + display name to avoid stuck loading.
  // Belt-and-braces: never use a service URI (qobuz:artist:123) as a display name or cache key.
  const isServiceUri = (s: string) => /^(qobuz|tidal|spotify):(artist|track|album):/i.test(s);
  const displayName = isServiceUri(safeName) ? '' : (safeName || ((!isUuid(rawId) && !isServiceUri(rawId)) ? rawId : ''));
  const requestId = makeRequestId();
  const key = makeKey('artist', args.artistId || null, displayName || null);

  dispatch({
    type: 'entity/openRequested',
    entityType: 'artist',
    key,
    id: args.artistId || null,
    name: displayName || null,
    hintKind: args.hintKind,
    meta: meta(requestId, 'loading', triggeredBy),
  });
  dispatch({ type: 'entity/loadingChanged', entityType: 'artist', key, value: true, meta: meta(requestId, 'loading', triggeredBy) });
  dispatch({ type: 'entity/enrichingChanged', entityType: 'artist', key, value: false, meta: meta(requestId, 'loading', triggeredBy) });

  if (args.preloadedPayload) {
    dispatch({
      type: 'entity/batonPreloadReceived',
      entityType: 'artist',
      key,
      payload: args.preloadedPayload,
      meta: meta(requestId, triggeredBy === 'baton' ? 'baton' : 'local', triggeredBy),
    });
  }

  const typeHint = args.typeHint || '';
  const lookupKey = isUuid(rawId) ? rawId : (displayName || null);

  // Preview fetch runs immediately (parallel, non-blocking).
  if (displayName) {
    fetch(`${CLOUD_API_URL}/api/preview/artist?q=${encodeURIComponent(displayName)}&typeHint=${encodeURIComponent(typeHint)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((preview) => {
        if (preview?.imageUrl) {
          dispatch({
            type: 'entity/previewReceived',
            entityType: 'artist',
            key,
            payload: { imageUrlHero: preview.imageUrl, imageUrl: preview.imageUrl },
            meta: meta(requestId, 'preview', triggeredBy),
          });
        }
      })
      .catch(() => {});
  }

  // QUICK FETCH: For name-based lookups, hit the lightweight DB-only endpoint first.
  // This returns in ~300ms vs 2-8s for the full cascade, so the page renders immediately.
  let quickData: any = null;
  let quickResolvedId: string | null = null;
  if (lookupKey && !isUuid(lookupKey)) {
    try {
      const qRes = await fetch(`${CLOUD_API_URL}/api/artist/quick?name=${encodeURIComponent(lookupKey)}&_cb=${Date.now()}`, { cache: 'no-store' });
      if (qRes.ok) {
        const qJson = await safeJson(qRes);
        if (qJson?.found && qJson?.name) {
          quickData = qJson;
          quickResolvedId = qJson.id || null;
          if (String(typeHint).toLowerCase() === 'composer' && quickData.type !== 'composer') {
            quickData.type = 'composer';
            quickData.genres = Array.isArray(quickData.genres)
              ? Array.from(new Set([...quickData.genres, 'composer', 'classical']))
              : ['composer', 'classical'];
          }
          dispatch({ type: 'entity/dbReceived', entityType: 'artist', key, payload: quickData, meta: meta(requestId, 'db', triggeredBy) });
          dispatch({ type: 'entity/loadingChanged', entityType: 'artist', key, value: false, meta: meta(requestId, 'db', triggeredBy) });
          log(`Quick fetch resolved in ${Date.now() - Date.now()}ms → id=${quickResolvedId}`);
        }
      }
    } catch {
      log('quick fetch failed, falling through to full endpoint');
    }
  }

  // DB fetch (await) — full endpoint with KV/mirror/enrichment cascade.
  // If quick fetch already populated the page, use the resolved ID for a richer background refresh.
  let artistData: any = null;
  let dbFetchFailed = false;
  const fetchStart = Date.now();
  const fullLookupKey = quickResolvedId || lookupKey;
  log(`Fetching artist: ${fullLookupKey}${quickData ? ' (background refresh after quick)' : ''}`);
  if (fullLookupKey) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const cacheBuster = `_cb=${Date.now()}`;
      const url = `${CLOUD_API_URL}/api/artists/${encodeURIComponent(fullLookupKey)}?${cacheBuster}`;
      const res = await fetch(url, { 
        signal: controller.signal,
        cache: 'no-store'
      });
      clearTimeout(timeoutId);
      log(`API response in ${Date.now() - fetchStart}ms, status: ${res.status}`);
      if (res.ok) {
        artistData = await safeJson(res);
        if (artistData?.name) {
          if (String(typeHint).toLowerCase() === 'composer' && artistData.type !== 'composer') {
            artistData.type = 'composer';
            artistData.genres = Array.isArray(artistData.genres)
              ? Array.from(new Set([...artistData.genres, 'composer', 'classical']))
              : ['composer', 'classical'];
          }
          // If quick data already rendered, this is an enriched merge; otherwise it's the primary dispatch.
          const eventType = quickData ? 'entity/enrichedReceived' : 'entity/dbReceived';
          dispatch({ type: eventType, entityType: 'artist', key, payload: artistData, meta: meta(requestId, quickData ? 'enriched' : 'db', triggeredBy) });
          dispatch({ type: 'entity/loadingChanged', entityType: 'artist', key, value: false, meta: meta(requestId, 'db', triggeredBy) });

          scheduleLinkMapRefresh({ entityType: 'artist', lookupKey: String(fullLookupKey), key, requestId, triggeredBy, initialPayload: artistData });
        }
      } else {
        dbFetchFailed = true;
      }
    } catch (e: any) {
      dbFetchFailed = true;
      console.error(`[EntityLoader] Artist fetch FAILED after ${Date.now() - fetchStart}ms:`, e?.message || e);
      log('artist db fetch failed', e?.message || e);
    }
  }

  // If the main artist fetch fails (common symptom: hero/portrait loads via preview, but story is blank),
  // keep retrying in the background for a short window so the page self-heals without a second tap.
  // Skip retries if quick data already rendered the page successfully.
  if (fullLookupKey && !artistData && dbFetchFailed && !quickData) {
    const retryKey = fullLookupKey;
    const scheduleMs = [1200, 2500, 4000, 6500, 9500, 14000, 20000];
    (async () => {
      const start = Date.now();
      for (let i = 0; i < scheduleMs.length; i++) {
        const elapsed = Date.now() - start;
        const waitMs = Math.max(0, scheduleMs[i] - elapsed);
        await new Promise((r) => setTimeout(r, waitMs));
        try {
          const refreshedRes = await fetch(`${CLOUD_API_URL}/api/artists/${encodeURIComponent(retryKey)}?_cb=${Date.now()}`, {
            cache: 'no-store',
          });
          if (refreshedRes.ok) {
            const refreshed = await safeJson(refreshedRes);
            if (refreshed?.name) {
              dispatch({ type: 'entity/dbReceived', entityType: 'artist', key, payload: refreshed, meta: meta(requestId, 'db', triggeredBy) });
              return;
            }
          }
        } catch {
          // ignore
        }
      }
    })();
  }

  // If we found an artist but the bio is not template-ready, proactively trigger enrichment + refresh.
  // IMPORTANT: do not block/poll just because the server flags data as “stale” when the bio is already usable.
  // (That creates the “hero loads fast, text takes forever” UX.)
  const enrichmentSource = artistData || quickData;
  if (enrichmentSource?.id) {
    const bio = extractAnyBio(enrichmentSource);
    const hasDisplayableContent = isDisplayableBio(bio);
    const hasTeaser = hasTeaserWithoutFullBio(enrichmentSource);
    const storyStatus = (enrichmentSource as any)?.storyStatus;
    const shouldBackgroundRefreshStory = isQueuedStatus(storyStatus) || (hasTeaser && !isTemplateReadyText(bio));

    // Only show the long-poll "enriching" UI when the BIO is actually missing AND we don't already have a teaser.
    // Many entities keep `enrichmentQueued=true` for discography/member refreshes even when the bio is already perfect.
    // Treating that as “enriching” causes the 20–40s "Updating..." banner on otherwise-complete pages.
    const showEnrichingSpinner = !hasTeaser && !hasDisplayableContent;
    if (showEnrichingSpinner) {
      dispatch({
        type: 'entity/enrichingChanged',
        entityType: 'artist',
        key,
        value: true,
        meta: meta(requestId, 'enriched', triggeredBy),
      });
    }

    // If server indicates story is queued OR we only have a teaser, keep refreshing in the background so
    // the full narrative can fade in automatically when it lands.
    if (!hasDisplayableContent || shouldBackgroundRefreshStory) {
      // Fire-and-forget enrichment kickoff (safe even if already queued)
      fetch(`${CLOUD_API_URL}/api/enrich/artist/${encodeURIComponent(enrichmentSource.id)}`, { method: 'POST' }).catch(() => {});

      pollStoryChunks({
        entityType: 'artist',
        entityId: String(enrichmentSource.id),
        key,
        requestId,
        triggeredBy,
        stopWhen: () => {
          const st: any = useMaestroStore.getState();
          const rec: any = st?.entities?.artists?.[key]?.data || null;
          return !!(rec?.biographyNarrative || rec?.biography);
        },
      }).catch(() => {});

      // Refresh schedule: absolute times since enrichment kickoff.
      // Goal: catch fast enrichments quickly, but ALSO keep the page self-healing for slower jobs.
      // We saw real cases where the DB bio became ready at ~60–90s; the UI must not require a reopen.
      // NOTE: absolute (not cumulative).
      const scheduleMs = [
        1200, 2500, 4000, 6500, 9500, 14000, 20000, 28000, 40000,
        60000, 90000, 120000, 180000,
      ];
      const doRefresh = async (): Promise<boolean> => {
        try {
          const refreshedRes = await fetch(`${CLOUD_API_URL}/api/artists/${encodeURIComponent(enrichmentSource.id)}?_cb=${Date.now()}`, {
            cache: 'no-store',
          });
          if (refreshedRes.ok) {
            const refreshed = await safeJson(refreshedRes);
            if (refreshed?.name) {
              dispatch({ type: 'entity/enrichedReceived', entityType: 'artist', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
              const newBio = extractAnyBio(refreshed);
              const done = isTemplateReadyText(newBio) || (!hasTeaser && isDisplayableBio(newBio));
              if (done) return true;
            }
          }
        } catch {
          // ignore
        }
        return false;
      };

      // Schedule refreshes; stop early once bio is displayable.
      (async () => {
        const start = Date.now();
        for (let i = 0; i < scheduleMs.length; i++) {
          const elapsed = Date.now() - start;
          const waitMs = Math.max(0, scheduleMs[i] - elapsed);
          await new Promise((r) => setTimeout(r, waitMs));
          const done = await doRefresh();
          if (done) {
            if (showEnrichingSpinner) {
              dispatch({ type: 'entity/enrichingChanged', entityType: 'artist', key, value: false, meta: meta(requestId, 'enriched', triggeredBy) });
            }
            return;
          }
        }
        // Timed out: stop the spinner but keep whatever partial data we have.
        if (showEnrichingSpinner) {
          dispatch({ type: 'entity/enrichingChanged', entityType: 'artist', key, value: false, meta: meta(requestId, 'enriched', triggeredBy) });
        }
      })();
    }
  }

  // If the DB fetch failed but we already rendered a teaser (BATON preload / cached navigation),
  // still do a background refresh loop so the full narrative can appear without requiring a second tap.
  if (!artistData && !quickData && args.preloadedPayload && (args.preloadedPayload as any)?.name) {
    const preloadHasTeaser = hasTeaserWithoutFullBio(args.preloadedPayload);
    const preloadLookup = isUuid(rawId) ? rawId : (displayName || null);
    if (preloadHasTeaser && preloadLookup) {
      // Global fix: if name-based lookups are brittle (accents/punctuation/aliases),
      // resolve the canonical DB artist id via /api/search and refresh by id.
      // This prevents "teaser stuck for 30-45s" even when the full bio is already ready.
      let resolvedLookup: string | null = null;
      try {
        const q = displayName || String((args.preloadedPayload as any)?.name || '').trim();
        if (q) {
          const r = await fetch(`${CLOUD_API_URL}/api/search?q=${encodeURIComponent(q)}&limit=3&_cb=${Date.now()}`, { cache: 'no-store' });
          const s = r && r.ok ? await safeJson(r) : null;
          const artists = Array.isArray(s?.artists) ? s.artists : [];
          const best = artists.find((a: any) => a?.inDatabase === true) || artists[0] || null;
          const id = best?.id || best?.external_id || best?.externalId || null;
          if (id) resolvedLookup = String(id);
        }
      } catch {
        resolvedLookup = null;
      }
      const lookup = resolvedLookup || String(preloadLookup);

      const scheduleMs = [
        1200, 2500, 4000, 6500, 9500, 14000, 20000, 28000, 40000,
        60000, 90000, 120000, 180000,
      ];
      (async () => {
        const start = Date.now();
        for (let i = 0; i < scheduleMs.length; i++) {
          const elapsed = Date.now() - start;
          const waitMs = Math.max(0, scheduleMs[i] - elapsed);
          await new Promise((r) => setTimeout(r, waitMs));
          try {
            const refreshedRes = await fetch(`${CLOUD_API_URL}/api/artists/${encodeURIComponent(lookup)}?_cb=${Date.now()}`, {
              cache: 'no-store',
            });
            if (refreshedRes.ok) {
              const refreshed = await safeJson(refreshedRes);
              if (refreshed?.name) {
                dispatch({ type: 'entity/enrichedReceived', entityType: 'artist', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
                const newBio = extractAnyBio(refreshed);
                if (isTemplateReadyText(newBio) || isDisplayableBio(newBio)) return;
              }
            }
          } catch {
            // ignore
          }
        }
      })();
    }
  }

  // Freshness check (non-blocking): for living/active/unknown entities, verify death/disband status and
  // trigger background re-enrichment if stale. Keeps the golden DB accurate without blocking initial render.
  if (enrichmentSource?.id) {
    try {
      const id = String(enrichmentSource.id);
      const endDate = enrichmentSource?.disbandDate || null;
      const isRefreshable = enrichmentSource?.lifeStatus?.refreshable ?? !endDate;
      if (isRefreshable && !shouldThrottleFreshness('artist', id)) {
        fetch(`${CLOUD_API_URL}/api/freshness/artist/${encodeURIComponent(id)}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((freshness) => {
            if (freshness?.reEnrichmentTriggered || freshness?.deathDateChanged) {
              setTimeout(async () => {
                const refreshedRes = await fetch(`${CLOUD_API_URL}/api/artists/${encodeURIComponent(id)}?_cb=${Date.now()}`).catch(() => null);
                if (refreshedRes && refreshedRes.ok) {
                  const refreshed = await safeJson(refreshedRes);
                  if (refreshed?.name) {
                    dispatch({ type: 'entity/enrichedReceived', entityType: 'artist', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
                  }
                }
              }, 1200);
            }
          })
          .catch(() => {});
      }
    } catch {
      // ignore
    }
  }

  // Background discography refresh - single fetch after 3s, no polling.
  // Use explicit server status when available; do not treat generic `enrichmentQueued` as “story missing”.
  const discStatus = (enrichmentSource as any)?.discographyStatus;
  const discLen = (Array.isArray(enrichmentSource?.discography) ? enrichmentSource.discography.length : 0) || 0;
  if (enrichmentSource?.id && (discStatus === 'queued' || discLen === 0)) {
    try {
      fetch(`${CLOUD_API_URL}/api/enrich/artist/${encodeURIComponent(String(enrichmentSource.id))}`, { method: 'POST' }).catch(() => {});
    } catch { /* ignore */ }

    try {
      const id = String(enrichmentSource.id);
      const last = discographyRefreshLastStart.get(id) || 0;
      const nowTs = Date.now();
      // Throttle: at most once per 45s per artist id.
      if (nowTs - last > 45000) {
        discographyRefreshLastStart.set(id, nowTs);

        const scheduleMs = [2500, 5000, 8500, 14000, 22000, 32000, 45000, 65000, 90000];
        (async () => {
          const start = Date.now();
          for (let i = 0; i < scheduleMs.length; i++) {
            const elapsed = Date.now() - start;
            const waitMs = Math.max(0, scheduleMs[i] - elapsed);
            await new Promise((r) => setTimeout(r, waitMs));
            try {
              const refreshedRes = await fetch(`${CLOUD_API_URL}/api/artists/${encodeURIComponent(id)}?_cb=${Date.now()}`, {
                cache: 'no-store',
              });
              if (!refreshedRes.ok) continue;
              const refreshed = await safeJson(refreshedRes);
              if (!refreshed?.name) continue;
              dispatch({ type: 'entity/enrichedReceived', entityType: 'artist', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
              const d = Array.isArray(refreshed?.discography) ? refreshed.discography : [];
              const st = String(refreshed?.discographyStatus || '').toLowerCase();
              if (d.length > 0) return;
              if (st && st !== 'queued' && st !== 'enriching' && st !== 'generating') return;
            } catch {
              // ignore
            }
          }
        })();
      }
    } catch {
      // ignore
    }
  }

  // Server now handles auto-import from external metadata sources, so client only needs to handle explicit MBID imports
  // No polling - just import and single refresh
  if ((!artistData || !artistData?.name) && !quickData && args.mbid) {
    try {
      const importRes = await fetch(`${CLOUD_API_URL}/api/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mbid: args.mbid, type: typeHint || 'artist', name: safeName }),
      });
      if (importRes.ok) {
        const importData = await safeJson(importRes);
        if (importData?.artist) {
          dispatch({ type: 'entity/mbReceived', entityType: 'artist', key, payload: importData.artist, meta: meta(requestId, 'mb', triggeredBy) });
        }
      }
    } catch { /* ignore */ }
  }

  dispatch({ type: 'entity/loadingChanged', entityType: 'artist', key, value: false, meta: meta(requestId, 'db', triggeredBy) });
  return { key, requestId };
}

export async function loadPerson(args: {
  personId?: string | null;
  personName?: string | null;
  triggeredBy?: 'ui' | 'baton' | 'system';
  preloadedPayload?: Record<string, any> | null;
}): Promise<{ key: string; requestId: string }> {
  const triggeredBy = args.triggeredBy || 'ui';
  const safeName = String(args.personName || '').replace(/^\s*[-–—]+\s*/, '').trim();
  const rawId = String(args.personId || '').trim();
  const displayName = safeName || (!isUuid(rawId) ? rawId : '');
  const requestId = makeRequestId();
  const key = makeKey('person', args.personId || null, displayName || null);

  dispatch({
    type: 'entity/openRequested',
    entityType: 'person',
    key,
    id: args.personId || null,
    name: displayName || null,
    meta: meta(requestId, 'loading', triggeredBy),
  });
  dispatch({ type: 'entity/loadingChanged', entityType: 'person', key, value: true, meta: meta(requestId, 'loading', triggeredBy) });
  dispatch({ type: 'entity/enrichingChanged', entityType: 'person', key, value: false, meta: meta(requestId, 'loading', triggeredBy) });

  if (args.preloadedPayload) {
    dispatch({
      type: 'entity/batonPreloadReceived',
      entityType: 'person',
      key,
      payload: args.preloadedPayload,
      meta: meta(requestId, triggeredBy === 'baton' ? 'baton' : 'local', triggeredBy),
    });
  }

  // Preview (parallel)
  if (displayName) {
    fetch(`${CLOUD_API_URL}/api/preview/artist?q=${encodeURIComponent(displayName)}&typeHint=person`)
      .then((r) => (r.ok ? r.json() : null))
      .then((preview) => {
        if (preview?.imageUrl) {
          dispatch({
            type: 'entity/previewReceived',
            entityType: 'person',
            key,
            payload: { imageUrl: preview.imageUrl },
            meta: meta(requestId, 'preview', triggeredBy),
          });
        }
      })
      .catch(() => {});
  }

  let personData: any = null;
  const searchTerm = (isUuid(rawId) ? rawId : (displayName || rawId)) || null;
  if (searchTerm) {
    try {
      const res = await fetch(`${CLOUD_API_URL}/api/contributors/${encodeURIComponent(searchTerm)}`);
      if (res.ok) {
        personData = await safeJson(res);
        if (personData?.name) {
          dispatch({ type: 'entity/dbReceived', entityType: 'person', key, payload: personData, meta: meta(requestId, 'db', triggeredBy) });
          dispatch({ type: 'entity/loadingChanged', entityType: 'person', key, value: false, meta: meta(requestId, 'db', triggeredBy) });

          // Dynamic verified links (same rationale as artists).
          scheduleLinkMapRefresh({ entityType: 'person', lookupKey: String(searchTerm), key, requestId, triggeredBy, initialPayload: personData });

          // Discography: `/api/contributors` does not currently return a full discography,
          // but many "people" in Maestro are also performer entities with releases.
          // Fetch from the artist endpoint in the background and merge into the person record.
          try {
            const hasDisc = Array.isArray((personData as any)?.discography) && (personData as any).discography.length > 0;
            const notable = Array.isArray((personData as any)?.notableAlbums) ? (personData as any).notableAlbums : [];
            if (!hasDisc && notable.length > 0) {
              // Provide an immediate useful shelf while the full discography is fetched.
              dispatch({
                type: 'entity/enrichedReceived',
                entityType: 'person',
                key,
                payload: { discography: notable },
                meta: meta(requestId, 'enriched', triggeredBy),
              });
            }
            if (!hasDisc && personData?.id) {
              setTimeout(async () => {
                try {
                  const aRes = await fetch(
                    `${CLOUD_API_URL}/api/artists/${encodeURIComponent(String(personData.id))}?_cb=${Date.now()}`,
                    { cache: 'no-store' }
                  );
                  if (!aRes.ok) return;
                  const a = await safeJson(aRes);
                  const d = Array.isArray(a?.discography) ? a.discography : [];
                  if (d.length > 0) {
                    dispatch({
                      type: 'entity/enrichedReceived',
                      entityType: 'person',
                      key,
                      payload: { discography: d, discographyStatus: a?.discographyStatus || 'ready' },
                      meta: meta(requestId, 'enriched', triggeredBy),
                    });
                  }
                } catch {
                  // ignore
                }
              }, 2200);
            }
          } catch {
            // ignore
          }
        }
      }
    } catch (e: any) {
      log('person db fetch failed', e?.message || e);
    }
  }

  // GLOBAL FIX: If server queued enrichment (common when navigating by UUID with no personName),
  // refresh automatically a couple times so the bio appears without requiring the user to tap again.
  // This complements the server-side async enrichment model (Claude calls can take 5–15s).
  if (personData?.name && searchTerm) {
    const bioNow = String(personData?.biographyNarrative || personData?.biography || '').trim();
    const storyStatus = (personData as any)?.storyStatus;
    const shouldRefresh =
      storyStatus === 'queued' ||
      !!personData?.enrichment?.needsEnrichment ||
      isPersonStub(personData) ||
      bioNow.length < 400;

    if (shouldRefresh) {
      // Incremental story chunks (progressive reveal). Additive — final bio still arrives via normal refresh.
      if (personData?.id) {
        pollStoryChunks({
          entityType: 'person',
          entityId: String(personData.id),
          key,
          requestId,
          triggeredBy,
          stopWhen: () => {
            const st: any = useMaestroStore.getState();
            const rec: any = st?.entities?.people?.[key]?.data || null;
            return !!(rec?.biographyNarrative || rec?.biography);
          },
        }).catch(() => {});
      }

      const doRefresh = async () => {
        try {
          const refreshedRes = await fetch(`${CLOUD_API_URL}/api/contributors/${encodeURIComponent(searchTerm)}?_cb=${Date.now()}`, {
            cache: 'no-store',
          });
          if (refreshedRes.ok) {
            const refreshed = await safeJson(refreshedRes);
            if (refreshed?.name) {
              dispatch({ type: 'entity/enrichedReceived', entityType: 'person', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
              const bio = String(refreshed?.biographyNarrative || refreshed?.biography || '').trim();
              return bio.length >= 400;
            }
          }
        } catch {
          // ignore
        }
        return false;
      };

      // Refresh at ~3s and ~8s. Stop early if we got a displayable bio.
      setTimeout(async () => {
        const done = await doRefresh();
        if (done) return;
        setTimeout(async () => {
          await doRefresh();
        }, 5000);
      }, 3000);
    }
  }

  // Person import - simplified, no blocking polls
  // Server now handles auto-import, so just trigger enrichment in background
  if ((!personData || !personData?.name || isPersonStub(personData)) && safeName) {
    // Fire-and-forget enrichment, then poll with increasing delays
    // Bio enrichment takes 5-15s (Claude API calls), so we need longer waits
    fetch(`${CLOUD_API_URL}/api/enrich/person/${encodeURIComponent(safeName)}`, { method: 'POST' }).catch(() => {});
    
    // First refresh at 3s (might catch fast enrichments)
    setTimeout(async () => {
      try {
        const refreshedRes = await fetch(`${CLOUD_API_URL}/api/contributors/${encodeURIComponent(safeName)}`);
        if (refreshedRes.ok) {
          const refreshed = await safeJson(refreshedRes);
          if (refreshed?.name) {
            dispatch({ type: 'entity/enrichedReceived', entityType: 'person', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
            // If bio is now present, we're done
            const bio = refreshed?.biographyNarrative || refreshed?.biography || '';
            if (bio.length >= 400) return;
          }
        }
      } catch { /* ignore */ }
      
      // Second refresh at 8s (most enrichments complete by now)
      setTimeout(async () => {
        try {
          const refreshedRes = await fetch(`${CLOUD_API_URL}/api/contributors/${encodeURIComponent(safeName)}`);
          if (refreshedRes.ok) {
            const refreshed = await safeJson(refreshedRes);
            if (refreshed?.name) {
              dispatch({ type: 'entity/enrichedReceived', entityType: 'person', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
            }
          }
        } catch { /* ignore */ }
      }, 5000);
    }, 3000);
  }

  // Freshness check (non-blocking)
  try {
    const id = personData?.id || args.personId;
    if (id) {
      fetch(`${CLOUD_API_URL}/api/freshness/person/${encodeURIComponent(id)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((freshness) => {
          if (freshness?.reEnrichmentTriggered || freshness?.dataUpdated) {
            setTimeout(async () => {
              const refreshRes = await fetch(`${CLOUD_API_URL}/api/contributors/${encodeURIComponent(searchTerm || safeName)}`).catch(() => null);
              if (refreshRes && refreshRes.ok) {
                const refreshed = await safeJson(refreshRes);
                if (refreshed?.name) {
                  dispatch({ type: 'entity/freshnessReceived', entityType: 'person', key, payload: refreshed, meta: meta(requestId, 'freshness', triggeredBy) });
                }
              }
            }, 3000);
          }
        })
        .catch(() => {});
    }
  } catch {}

  dispatch({ type: 'entity/loadingChanged', entityType: 'person', key, value: false, meta: meta(requestId, 'db', triggeredBy) });
  return { key, requestId };
}

export async function loadAlbum(args: {
  albumId: string | number;
  prefill?: Record<string, any>;
  triggeredBy?: 'ui' | 'baton' | 'system';
}): Promise<{ key: string; requestId: string }> {
  const triggeredBy = args.triggeredBy || 'ui';
  const requestId = makeRequestId();
  const key = makeKey('album', String(args.albumId), args.prefill?.title || null);
  const navSource = String(args.prefill?.source || 'unknown');
  logAlbumNavBreadcrumb('fetch_start', String(args.albumId), {
    source: navSource,
    triggered_by: triggeredBy,
    request_id: requestId,
  });

  const normalizeForCompare = (v: unknown): string =>
    String(v || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[’'"]/g, '')
      .replace(/[\/•|]+/g, ' ')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const looksTemplatedNarrative = (textRaw: unknown): boolean => {
    const text = String(textRaw || '').trim();
    if (!text) return false;
    if (/##\s*overview/i.test(text)) return true;
    if (/##\s+\S+/i.test(text) && /\n---\n/.test(text)) return true;
    return false;
  };

  const looksLikeRawWikipedia = (textRaw: unknown): boolean => {
    const text = String(textRaw || '').trim();
    if (!text) return false;
    // Heuristics: Wikipedia hatnotes + common lead markers.
    if (/\bthis article is about\b/i.test(text)) return true;
    if (/\bfrom wikipedia\b/i.test(text)) return true;
    if (/\bmay refer to:\b/i.test(text)) return true;
    return false;
  };

  dispatch({
    type: 'entity/openRequested',
    entityType: 'album',
    key,
    id: String(args.albumId),
    name: args.prefill?.title || null,
    meta: meta(requestId, 'loading', triggeredBy),
  });
  dispatch({ type: 'entity/loadingChanged', entityType: 'album', key, value: true, meta: meta(requestId, 'loading', triggeredBy) });
  dispatch({ type: 'entity/enrichingChanged', entityType: 'album', key, value: false, meta: meta(requestId, 'loading', triggeredBy) });

  if (args.prefill) {
    dispatch({ type: 'entity/dbReceived', entityType: 'album', key, payload: args.prefill, meta: meta(requestId, 'local', triggeredBy) });
  }

  // Qobuz-sourced albums: the prefill already has the correct identity (title, cover,
  // serviceAlbumId). AlbumDetailView independently fetches full Qobuz tracks/metadata
  // via the Qobuz API. The generic DB lookup below would either 404 (Qobuz IDs aren't
  // MusicBrainz UUIDs) or return a WRONG album that overwrites the correct prefill.
  // The search fallback is even worse — it switches currentKey to a different entity,
  // causing the page to visually "swap" to a different album. Skip both.
  const prefillId = String(args.prefill?.serviceAlbumId || args.prefill?.id || '');
  const isQobuzSourced = !!(args.prefill?.source === 'qobuz' && prefillId && !isUuid(prefillId));

  let albumData: any = null;
  if (!isQobuzSourced) {
    try {
      const res = await fetch(`${CLOUD_API_URL}/api/albums/${encodeURIComponent(String(args.albumId))}?_cb=${Date.now()}`, {
        cache: 'no-store',
      });
      if (res.ok) {
        albumData = await safeJson(res);
        if (albumData) {
          dispatch({ type: 'entity/dbReceived', entityType: 'album', key, payload: albumData, meta: meta(requestId, 'db', triggeredBy) });
        }
      }
    } catch (e: any) {
      log('album fetch failed', e?.message || e);
    }
  } else {
    log(`album ${key}: Qobuz-sourced, skipping DB lookup (AlbumDetailView handles Qobuz data)`);
  }

  // Fallback: if primary fetch didn't return data and we have title+artist from prefill,
  // resolve the correct album via search API (handles demo IDs, slugs, etc.).
  //
  // IMPORTANT: entity keys are derived from args.albumId, so if we resolve to a new id,
  // re-enter loadAlbum with the resolved id so the store's currentKey updates correctly.
  //
  // Skipped for Qobuz-sourced albums — the search can match the wrong album and the
  // recursive loadAlbum switches currentKey, causing the page to render a different album.
  if (!isQobuzSourced && !albumData && args.prefill?.title) {
    try {
      const searchTitle = String(args.prefill.title || '').trim();
      const searchArtist = String(args.prefill.artist || args.prefill.artistName || args.prefill.artist_name || '').trim();
      if (searchTitle) {
        const queries = searchArtist ? [`${searchTitle} ${searchArtist}`, searchTitle] : [searchTitle];
        let match: any = null;

        for (let qi = 0; qi < queries.length && !match; qi++) {
          const q = encodeURIComponent(queries[qi]);
          const searchController = new AbortController();
          const searchTimeoutId = setTimeout(() => searchController.abort(), 4500);
          const searchRes = await fetch(`${CLOUD_API_URL}/api/search?type=albums&q=${q}&limit=${searchArtist ? 20 : 10}`, {
            signal: searchController.signal,
          });
          clearTimeout(searchTimeoutId);
          if (!searchRes.ok) continue;

          const searchData = await safeJson(searchRes);
          const results = Array.isArray(searchData?.albums) ? searchData.albums : [];

          const titleNorm = normalizeForCompare(searchTitle);
          const artistNorm = normalizeForCompare(searchArtist);

          const candidatesByTitle = results.filter((r: any) => normalizeForCompare(r?.title).includes(titleNorm));
          const candidatesByArtist = artistNorm
            ? candidatesByTitle.filter((r: any) => {
              const rArtist = normalizeForCompare(r?.artist_name || r?.artistName || '');
              const rTitle = normalizeForCompare(r?.title || '');
              return rArtist.includes(artistNorm) || artistNorm.includes(rArtist) || rTitle.includes(artistNorm);
            })
            : candidatesByTitle;

          match = (candidatesByArtist[0] || candidatesByTitle[0] || results[0]) || null;
        }

        const resolvedIdRaw = match?.external_id || match?.externalId || match?.internalId || match?.internal_id || match?.id || null;
          const resolvedId = String(resolvedIdRaw || '').trim();
          if (resolvedId && resolvedId !== String(args.albumId)) {
            log(`album search fallback: ${args.albumId} → ${resolvedId} (${match?.title || 'unknown'})`);

            // Close out the current (bad-id) record so UI doesn't remain in a loading state.
            dispatch({ type: 'entity/loadingChanged', entityType: 'album', key, value: false, meta: meta(requestId, 'db', triggeredBy) });

            const mergedPrefill = {
              ...(args.prefill || {}),
              id: resolvedId,
              external_id: match?.external_id || match?.externalId || (args.prefill as any)?.external_id,
              serviceAlbumId: (args.prefill as any)?.serviceAlbumId || (args.prefill as any)?.id,
              source: (args.prefill as any)?.source,
              artistId: match?.artist_id || match?.artistId || (args.prefill as any)?.artistId,
              artistName: match?.artist_name || match?.artistName || (args.prefill as any)?.artist || (args.prefill as any)?.artistName,
              cover: match?.cover_url || match?.coverUrl || (args.prefill as any)?.cover,
              year: match?.release_year || match?.releaseYear || (args.prefill as any)?.year,
              title: match?.title || (args.prefill as any)?.title,
            };

            return await loadAlbum({
              albumId: resolvedId,
              prefill: mergedPrefill,
              triggeredBy,
            });
          }
      }
    } catch (e: any) {
      log('album search fallback failed', e?.message || e);
    }
  }

  const narrative = albumData?.biographyNarrative || albumData?.biography_narrative || '';
  const description = albumData?.description || '';

  // NOTE: We should never treat raw Wikipedia (or other untemplated dumps) as "complete".
  // Albums should converge to the Claude template narrative (sections + divider).
  const hasTemplatedNarrative = looksTemplatedNarrative(narrative) || looksTemplatedNarrative(description);
  const hasRichContent =
    hasTemplatedNarrative ||
    albumData?.recordingHistory ||
    albumData?.recording_history ||
    albumData?.bandEraContext ||
    albumData?.band_era_context;

  const albumSeemsQueued =
    String(albumData?.storyStatus || '').toLowerCase() === 'queued' ||
    albumData?.enriching === true ||
    albumData?.needsEnrichment === true;

  // Albums often hydrate asynchronously (mirror tracks/credits + narrative).
  // If we don't have rich content yet OR server indicates queued, trigger enrich and keep refreshing.
  if (albumData && (!hasRichContent || albumSeemsQueued)) {
    dispatch({ type: 'entity/enrichingChanged', entityType: 'album', key, value: true, meta: meta(requestId, 'enriched', triggeredBy) });
    try {
      // Fire-and-forget enrichment kickoff (safe even if already queued)
      fetch(`${CLOUD_API_URL}/api/enrich/album/${encodeURIComponent(String(args.albumId))}`, { method: 'GET' }).catch(() => {});

      const isAlbumDisplayable = (payload: any) => {
        const bio = String(payload?.biographyNarrative || payload?.biography_narrative || '').trim();
        const desc = String(payload?.description || '').trim();
        const text = (bio || desc).trim();
        if (!text) return false;
        if (looksLikeRawWikipedia(text)) return false;
        // Albums can be shorter than artists, but they must be templated.
        return looksTemplatedNarrative(text) && text.length >= 220;
      };

      const doRefresh = async (): Promise<boolean> => {
        try {
          const refreshedRes = await fetch(`${CLOUD_API_URL}/api/albums/${encodeURIComponent(String(args.albumId))}?_cb=${Date.now()}`, {
            cache: 'no-store',
          });
          if (!refreshedRes.ok) return false;
          const refreshed = await safeJson(refreshedRes);
          if (refreshed) {
            dispatch({ type: 'entity/enrichedReceived', entityType: 'album', key, payload: refreshed, meta: meta(requestId, 'enriched', triggeredBy) });
            // Stop once we have a real narrative (or server stopped saying it's queued).
            if (isAlbumDisplayable(refreshed)) return true;
            const queued = refreshed?.enriching === true || refreshed?.needsEnrichment === true || String(refreshed?.storyStatus || '').toLowerCase() === 'queued';
            return !queued && isAlbumDisplayable(refreshed);
          }
        } catch {
          // ignore
        }
        return false;
      };

      const scheduleMs = [2000, 5000, 10000, 15000];
      (async () => {
        const start = Date.now();
        for (let i = 0; i < scheduleMs.length; i++) {
          const elapsed = Date.now() - start;
          const waitMs = Math.max(0, scheduleMs[i] - elapsed);
          await new Promise((r) => setTimeout(r, waitMs));
          const done = await doRefresh();
          if (done) return;
        }
      })();
    } catch (e: any) {
      dispatch({ type: 'entity/error', entityType: 'album', key, error: e?.message || 'Album enrichment failed', meta: meta(requestId, 'enriched', triggeredBy) });
    } finally {
      dispatch({ type: 'entity/enrichingChanged', entityType: 'album', key, value: false, meta: meta(requestId, 'enriched', triggeredBy) });
    }
  }

  dispatch({ type: 'entity/loadingChanged', entityType: 'album', key, value: false, meta: meta(requestId, 'db', triggeredBy) });
  if (albumData || isQobuzSourced || args.prefill) {
    logAlbumNavBreadcrumb('fetch_ok', String(args.albumId), {
      source: navSource,
      has_db_payload: !!albumData,
      qobuz_sourced: isQobuzSourced,
      request_id: requestId,
    });
  } else {
    logAlbumNavFailure(String(args.albumId), 'album entity load returned no data', {
      source: navSource,
      request_id: requestId,
    });
  }
  return { key, requestId };
}

